import { motion } from "framer-motion";

// Refined glass "O" ring mark — represents intelligence, connection, continuity.
export function OrayaMark({ size = 26, className = "", spin = false }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      className={`${className} ${spin ? "animate-spin-slow" : ""}`}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="oraya-ring" x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FFFFFF" />
          <stop offset="0.5" stopColor="#8f8f8f" />
          <stop offset="1" stopColor="#3a3a3a" />
        </linearGradient>
      </defs>
      <circle cx="24" cy="24" r="17" stroke="url(#oraya-ring)" strokeWidth="3.5" />
      <circle cx="24" cy="24" r="17" stroke="#FFFFFF" strokeOpacity="0.25" strokeWidth="0.75" />
      <circle cx="19" cy="17" r="1.4" fill="#FFFFFF" fillOpacity="0.9" />
    </svg>
  );
}

// ORAYA wordmark (public brand — never shows the legal name)
export function OrayaWordmark({ className = "", withMark = true, size = 26 }) {
  return (
    <span className={`flex items-center gap-2.5 select-none ${className}`}>
      {withMark && <OrayaMark size={size} />}
      <span className="display font-medium tracking-[0.28em] text-ink" style={{ fontSize: size * 0.72 }}>
        ORAYA
      </span>
    </span>
  );
}

export function Reveal({ children, delay = 0, y = 20, className = "" }) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function Overline({ children, className = "" }) {
  return (
    <span className={`text-xs font-semibold tracking-[0.25em] uppercase text-subtle ${className}`}>
      {children}
    </span>
  );
}

export function SectionHeader({ overline, title, subtitle, align = "left", light = false }) {
  return (
    <div className={`flex flex-col gap-5 mb-14 md:mb-20 ${align === "center" ? "items-center text-center max-w-3xl mx-auto" : "max-w-3xl"}`}>
      {overline && <Overline>{overline}</Overline>}
      <h2 className={`display text-4xl sm:text-5xl font-light tracking-tight leading-[1.05] ${light ? "text-invert-ink" : "text-ink"}`}>
        {title}
      </h2>
      {subtitle && <p className="text-lg text-subtle leading-relaxed max-w-2xl">{subtitle}</p>}
    </div>
  );
}

export function GlassCard({ children, className = "", variant = "heavy", hover = false, ...rest }) {
  const base = variant === "medium" ? "glass-medium" : "glass-heavy";
  return (
    <div className={`${base} rounded-3xl ${hover ? "hover-lift hover:border-hairline/20" : ""} ${className}`} {...rest}>
      {children}
    </div>
  );
}

// Seo — React 19 hoists title/meta rendered anywhere into <head>.
export function Seo({ title, description, path = "", type = "website", image }) {
  const url = `https://oraya-platform.preview.emergentagent.com${path}`;
  const desc = description || "ORAYA builds AI-native operating systems for complex real-world industries.";
  return (
    <>
      <title>{title}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={desc} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      {image && <meta property="og:image" content={image} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={desc} />
      {image && <meta name="twitter:image" content={image} />}
    </>
  );
}
